import { TFunction } from "i18next";
import { CompanyDto } from "model/Company";
import { CompanyRole } from "model/User";
import { Setting } from "model/Workgroup";

export interface NavbarMenuItem {
  href: string;
  iconName: string;
  label: string;
  value: string;
  badgeCount?: number;
}

export interface DropdownMenuItem {
  avatarSrc?: string;
  href: string;
  iconName: string;
  isExpandable?: boolean;
  isSelected?: boolean;
  label: string;
  subItems?: DropdownMenuItem[];
  tone?: "danger";
  value: string;
}

/**
 * Generates the list of main navigation items based on user type and role.
 *
 * @returns Array of navigation items for the NavbarMenu.
 */
export const getNavbarMenuItems = (
  userType: string | null,
  role: CompanyRole | undefined,
  t: TFunction,
  settings: Setting[] | undefined,
): NavbarMenuItem[] => {
  if (role === CompanyRole.COMPANY_TIMESTAMP) {
    return [];
  }

  const havePublicModule =
    settings?.find((s: Setting) => s.key === "CompanyShowPublicGigModule")
      ?.value === "true";

  const haveAnalysisSetting =
    settings?.find((s: Setting) => s.key === "AnalysisEnabled")?.value ===
    "true";

  switch (userType) {
    case "Company":
      return [
        {
          value: "schedule",
          label: t("Schedule.ScheduleTitle"),
          href: "/company/schedule",
          iconName: "v2/calendar",
        },
        {
          value: "time-reports",
          label: t("CompanyTimeReport.TimeReports"),
          href: "/company/time-reports",
          iconName: "v2/clock",
        },
        {
          value: "staff",
          label: t("Staff.Staff"),
          href: "/company/staff",
          iconName: "v2/person",
        },
        {
          value: "organisation",
          label: t("OrganizationalStructure.Organization"),
          href: "/company/organizationalStructure",
          iconName: "v2/person",
        },
        {
          value: "salary",
          label: t("Salary.Salary"),
          href: "/company/salary",
          iconName: "v2/wallet",
        },
        ...(havePublicModule
          ? [
              {
                value: "recruit",
                label: t("ApplicationsCompany.Recruit"),
                href: "/company/applications",
                iconName: "v2/plus-person",
              },
            ]
          : []),
        ...(haveAnalysisSetting
          ? [
              {
                value: "analytics",
                label: t("CompanyAnalytics.Analytics"),
                href: "/company/analytics",
                iconName: "v2/list",
              },
            ]
          : []),
      ];

    case "Employee":
      return [
        {
          value: "schedule",
          label: t("CompanyEmployee.Schedule"),
          href: "/employee/schedule",
          iconName: "v2/calendar",
        },
        {
          value: "profile",
          label: t("CompanyProfile.Profile"),
          href: "/employee/profile",
          iconName: "v2/person",
        },
      ];

    default:
      return [];
  }
};

/**
 * Generates the list of items for the user profile and company dropdown.
 *
 * @returns Array of menu items for the DropdownMenu.
 */
export const getDropdownMenuItems = (
  t: TFunction,
  userType: string | null,
  role: CompanyRole | undefined,
  companies: CompanyDto[] = [],
  currentCompany?: CompanyDto | null,
): DropdownMenuItem[] => {
  const baseItems: DropdownMenuItem[] = [
    {
      value: "profile",
      label: t("ProfileCompany.Profile"),
      href: "/company/profile",
      iconName: "v2/person",
    },
    {
      value: "settings",
      label: t("General.Settings"),
      href: "/company/settings",
      iconName: "v2/settings",
    },
    {
      value: "companies",
      label: t("CompanySelect.YourCompanies"),
      href: "/company/company-select",
      iconName: "v2/list",
      isExpandable: companies.length > 0,
      subItems: companies.map((c: CompanyDto) => ({
        value: `switch-${c.id}`,
        label: c.companyName || "",
        href: `/company/switch/${c.id}`,
        iconName: "v2/company",
        avatarSrc: c.companyImage || undefined,
        isSelected: c.id === currentCompany?.id,
      })),
    },
    {
      value: "add",
      label: t("CompanySelect.AddCompany"),
      href: "/company/registration",
      iconName: "v2/plus",
    },
    {
      value: "signout",
      label: t("ProfileCompany.SignOut"),
      href: "/company/login",
      iconName: "v2/logout",
      tone: "danger",
    },
  ];

  if (userType === "Company" && role !== CompanyRole.COMPANY_TIMESTAMP) {
    return baseItems;
  }

  return [
    {
      value: "staff-register-export",
      label: t("TimeReports.StaffRegister"),
      href: "/company/stampclock",
      iconName: "v2/download",
    },
    ...baseItems.filter((item: DropdownMenuItem) => item.value === "signout"),
  ];
};
